import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateSIP, formatCurrency } from '../utils/calculations';
import { generateSIPPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const sipFaqs = [
  {
    question: 'What is SIP in mutual funds?',
    answer: 'SIP stands for Systematic Investment Plan. It lets you invest a fixed amount at regular intervals, usually monthly, into a mutual fund.',
  },
  {
    question: 'Does SIP guarantee returns?',
    answer: 'No. SIP returns are market-linked and depend on the actual fund performance over time. This calculator only estimates growth based on your expected return input.',
  },
  {
    question: 'Can I start SIP with a small monthly amount?',
    answer: 'Yes. Many mutual funds allow you to begin with a relatively small monthly SIP amount and increase it later as your income grows.',
  },
  {
    question: 'Why does time period matter so much in SIP investing?',
    answer: 'A longer investment period gives compounding more time to work, which can materially increase the final corpus compared to short durations.',
  },
];

const SIPCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [years, setYears] = useState(10);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [monthlyInvestment, expectedReturn, years]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const m = Number(monthlyInvestment) || 0;
    const r = Number(expectedReturn) || 0;
    const y = Number(years) || 0;
    const sipResult = calculateSIP(m, r, y);
    setResult(sipResult);
  };

  const handleDownloadPDF = () => {
    generateSIPPDF({
      monthlyInvestment,
      expectedReturn,
      years,
      ...result,
    });
  };

  const handleReset = () => {
    setMonthlyInvestment(10000);
    setExpectedReturn(12);
    setYears(10);
  };

  const chartData = result ? {
    labels: ['Invested Amount', 'Est. Returns'],
    datasets: [{
      data: [result.totalInvested, result.wealthGained],
      backgroundColor: ['#2563eb', '#10b981'],
      borderColor: ['#1d4ed8', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="SIP Calculator"
        description="Free SIP Calculator India - Calculate returns on Systematic Investment Plan. See how monthly investments grow over time with compound interest. Export results as PDF."
        keywords="SIP calculator, mutual fund SIP calculator, SIP return calculator, systematic investment plan calculator, monthly investment calculator India"
        canonicalPath="/sip"
        calculatorType="SIP Calculator"
        faqItems={sipFaqs}
      />
      <div className="calculator-header">
        <h1>SIP Calculator</h1>
        <p>Plan your Systematic Investment Plan and see your wealth grow</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Monthly Investment (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={monthlyInvestment === '' ? '' : monthlyInvestment}
                onChange={(e) => setMonthlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
                min="500"
                max="1000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="500"
                max="100000"
                step="500"
                value={monthlyInvestment || 0}
                onChange={(e) => setMonthlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((monthlyInvestment || 0) / 100000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Expected Return Rate (% p.a.)</label>
            <input
              type="number"
              value={expectedReturn === '' ? '' : expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="30"
              step="0.5"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={expectedReturn || 0}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((expectedReturn || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={years === '' ? '' : years}
              onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="40"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="40"
                step="1"
                value={years || 0}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((years || 0) / 40) * 100}%` }}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Total Value</h3>
                <div className="amount">{formatCurrency(result.futureValue)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Invested Amount</span>
                  <span className="value">{formatCurrency(result.totalInvested)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Est. Returns</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.wealthGained)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Total Months</span>
                  <span className="value">{years * 12}</span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} options={chartOptions} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About SIP</h4>
        <p>
          SIP (Systematic Investment Plan) is an investment method where you invest a fixed amount 
          regularly in mutual funds. Benefits include:
        </p>
        <ul>
          <li>Rupee Cost Averaging - buy more units when prices are low</li>
          <li>Power of Compounding - earn returns on returns</li>
          <li>Disciplined Investing - builds regular saving habit</li>
          <li>Flexibility - start with as low as ₹500/month</li>
        </ul>
      </div>

      <section className="info-box" style={{ marginTop: '24px' }}>
        <h4>SIP Calculator FAQs</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {sipFaqs.map((item) => (
            <details key={item.question} style={{ border: '1px solid #dbe3f0', borderRadius: 12, background: '#fff', padding: '14px 16px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>{item.question}</summary>
              <p style={{ margin: '12px 0 0', color: '#475569', lineHeight: 1.65 }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SIPCalculator;
